import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from "react-router-dom";
import {
  ArrowBack,
} from "@mui/icons-material";
import { Button, Fab, Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { Gift } from "@dndModels";
import { makeStyles } from "@utilities/theme_utilities";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import CheckBox from "@components/input/CheckBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function GiftParseAdd() {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [old_gifts, setOldGifts] = useState<Gift[]>([]);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [parse_string, setParseString] = useState("");
  const [processing, setProcessing] = useState(false);
  const [complete, setComplete] = useState(false);
  const [processing_message, setProcessingMessage] = useState("");
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [expanded_gift, setExpandedGift] = useState("");
  const [make_dominant, setMakeDominant] = useState(false);
  const [final_report, setFinalReport] = useState<string[]>([]);

  const parseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  });
  
  const load = () => {
    setLoading(true);
    api.getSetOfObjects({ data_types: ["gift"]}).then(res => {
      setOldGifts(res.gift as Gift[]);
      setLoading(false);
    });
    setFetched(true);
  }
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

  const { classes, cx } = useStyles();

  const submit = (error_messages: string[] = []) => {
    // debugger;
    const next_gift = gifts.find(o => o._id === "");
    if (next_gift) {
      // if (make_dominant && next_gift.variant_group.length > 0) {
      //   const max_dominance_level = Math.max(...next_gift.variant_group.map(o => o.dominance_level));
      //   next_gift.dominance_level = max_dominance_level + 1;
      // }
      if (next_gift.parse_errors.length > 0) {
        error_messages.push(...next_gift.parse_errors.map(o => {
          return `${next_gift.name}: ${o}`;
        }));
      }
      setProcessingMessage(`Saving ${next_gift.name}`);
      api.upsertObject(next_gift.data_type, next_gift).then(res => {
        console.log(res);
        if (res && res.body.id) {
          next_gift._id = res.body.id;
        } else {
          next_gift._id = "Error";
          error_messages.push(`${next_gift.name} Save Failed`);
        }
        submit(error_messages);
      });
    } else {
      setFinalReport(error_messages);
      setProcessing(false);
      setComplete(true);
    }
  }

  const stripAttributes = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    const stripFromNode = (node: Node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        while (element.attributes.length > 0) {
          element.removeAttribute(element.attributes[0].name);
        }
        Array.from(element.childNodes).forEach(stripFromNode);
      }
    };
    
    Array.from(temp.childNodes).forEach(stripFromNode);
    return temp.innerHTML;
  };

  const parse = () => {
    let elem = parseRef.current as HTMLElement;
    while (elem instanceof HTMLDivElement && elem.children.length < 2) {
      elem = elem.children[0] as HTMLElement;
    }
    
    const new_gifts: Gift[] = [];
    let i = 0;
    
    // Find the first gift (H3 tag)
    while (i < elem.children.length) {
      const child = elem.children[i];
      ++i;
      if (child instanceof HTMLElement && 
          child.tagName === "H3" && 
          child.className.includes("heading-anchor")) {
        --i;
        break;
      }
    }
    
    let current_gift: Gift | null = null;
    let gift_type_line = "";

    const addGift = () => {
      if (current_gift && current_gift.name.length > 0) {
        const cg = current_gift;
        const possible_variants = old_gifts.filter(o => o.name === cg.name);
        let variant_group_ids: string[] = [];
        for (const pv of possible_variants) {
          variant_group_ids.push(pv.variant_group_id);
          current_gift.variant_ids = [...pv.variant_ids, ...current_gift.variant_ids];
        }
        variant_group_ids = common_util.unique_array(variant_group_ids);
        current_gift.variant_ids = common_util.unique_array(current_gift.variant_ids);
        // variant_group_ids = [...new Set(variant_group_ids)];
        // current_gift.variant_ids = [...new Set(current_gift.variant_ids)];
        
        if (variant_group_ids.length === 1) {
          current_gift.variant_of_id = variant_group_ids[0];
          current_gift.variant_group = possible_variants;
        }
        
        if (api.working_on_sb) {
          const sb_id = api.working_on_sb._id;
          current_gift.source_books_old = [{ 
            id: api.working_on_sb._id, 
            type: api.working_on_sb.source_book_type, 
            third_party: api.working_on_sb.third_party 
          }];
          if (possible_variants.filter(o => o.source_book_ids.includes(sb_id)).length > 0) {
            current_gift.parse_errors.push("Variant on Book Already Exists.");
          }
        }
        // console.log(current_gift.description);

        current_gift.description = common_util.cleanSpaces(current_gift.description);
        // console.log(current_gift.description);
        // current_gift.description = current_gift.description.replace(/\s+/g, ' ').trim();
        // console.log(current_gift.description);
        
        current_gift.description += "\r\n\r\nNOTE: This hasn't been fully implemented yet. If you are using it and it could use some work, please make a bug report using the bug button in the bar at the top of the page.";
        
        // console.log(current_gift.description);

        new_gifts.push(current_gift);
      }
    };

    while (i < elem.children.length) {
      const child = elem.children[i];
      ++i;
      
      if (!(child instanceof HTMLElement)) {
        continue;
      }

      if (child.tagName === "ASIDE" || child.tagName === "FIGURE") {
        continue;
      }

      if (child.tagName === "H3" && child.className.includes("heading-anchor")) {
        addGift();
        
        current_gift = new Gift();
        current_gift.name = child.textContent ? child.textContent.trim() : "";
        current_gift.name = current_gift.name.replace("'", "'");
        gift_type_line = "";
        continue;
      }

      if (current_gift) {
        // debugger;
        if (child.tagName === "P") {
          const text = child.textContent ? child.textContent.trim() : "";
          const inner_html = child.innerHTML;
          
          if (text && child.querySelector('em') && !gift_type_line) {
            gift_type_line = text;
            
            // Add this line to the description
            current_gift.description += text + "\r\n\r\n";
            
            // Parse item type and rarity
            // Remove anything in parentheses for parsing
            const clean_text = text.replace(/\([^)]*\)/g, '').trim();
            const pieces = clean_text.split(",");
            
            if (pieces.length >= 2) {
              current_gift.type = text;
            }
          } else if (inner_html) {
            // This is a description paragraph
            // Strip attributes but keep tags
            const cleaned_html = stripAttributes(inner_html);
            current_gift.description += cleaned_html + "\r\n\r\n";
          }
        }
      }
    }
    
    addGift();
    setGifts(new_gifts);
  }

  const renderGifts = () => {
    return (
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={0} direction="row">
          <Grid
            className="header centered"
            size={{
              xs: 12
            }}>Gifts Ready to Add</Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              name="Make Dominant"
              value={make_dominant}
              onChange={setMakeDominant}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={processing}
              onClick={() => { 
                setGifts(gifts.filter(o => !o.parse_errors.includes("Variant on Book Already Exists."))); 
              }}>
              Delete All Already in Source
            </Button>
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={processing}
              onClick={() => {
                setProcessing(true);
                submit();
              }}>
              Save All
            </Button>
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={processing}
              onClick={() => { setGifts([]); }}>
              Cancel
            </Button>
          </Grid>
          {gifts.map(gift => {
            return (
              <Grid
                key={gift.name}
                className="padded"
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row" className={cx(classes.root)}>
                  <Grid
                    onClick={() => { 
                      setExpandedGift(expanded_gift === gift.name ? "" : gift.name); 
                    }}
                    size={{
                      xs: 6
                    }}>
                    <div style={{ padding: '8px' }}>
                      <strong>{gift.name}</strong>
                    </div>
                  </Grid>
                  <Grid
                    onClick={() => { 
                      setExpandedGift(expanded_gift === gift.name ? "" : gift.name); 
                    }}
                    size={{
                      xs: 4
                    }}>
                    {gift.type || "Unknown Type"}
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <DeleteButton
                      onClick={() => {
                        if (expanded_gift === gift.name) {
                          setExpandedGift("");
                        }
                        setGifts(gifts.filter(o => o.name !== gift.name));
                      }}
                    />
                  </Grid>
                  {gift.parse_errors.map((error, key) => {
                    return (
                      <Grid
                        key={key}
                        className="small_label red"
                        size={{
                          xs: 12
                        }}>
                        {error}
                      </Grid>
                    );
                  })}
                  {expanded_gift === gift.name &&
                    <Grid
                      className="padded"
                      size={{
                        xs: 12
                      }}>
                      <div className="blackBackground">
                        <DisplayStringWithReplacements display_string={gift.description} />
                      </div>
                    </Grid>
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  }

  const renderParser = () => {
    return (
      <>
        <Grid
          size={{
            xs: 9
          }}>
          <StringBox 
            name="Parse String"
            value={parse_string}
            onBlur={setParseString}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <Button
            variant="contained"
            color="primary"
            disabled={processing || parse_string.length === 0}
            onClick={parse}>
            Parse
          </Button>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <div ref={parseRef} dangerouslySetInnerHTML={{ __html: parse_string }}></div>
        </Grid>
      </>
    );
  }

  if (!fetched || loading) {
    const height = window.innerHeight - 81;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth - 1} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <Tooltip title={`Back to Gifts`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={() => {
                setNavigateTo(`/gift`);
              }}>
              <ArrowBack/>
            </Fab>
          </Tooltip>&nbsp;
          <span className="header">
            Parse Gifts
          </span>
        </Grid>
        {processing &&
          <Grid
            className="centered"
            size={{
              xs: 12
            }}>
            <LoadingBox message={processing_message} height={200} />
          </Grid>
        }
        {!processing && !complete && 
          <>
            {gifts.length > 0 ? renderGifts() : renderParser()}
          </>
        }
        {complete &&
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Log"
              value={final_report.join("\r\n")}
              multiline
            />
          </Grid>
        }
      </Grid>
    );
  }
}